import { typingStrip, type KeystrokeLog, type StripBucket } from '@coderaw/log'
import { el } from '../../lib/dom'

export type TypingStrip = {
  readonly element: HTMLCanvasElement
  /** Moves the playhead to a real-time position in ms. */
  readonly setPlayhead: (realMs: number) => void
}

const HEIGHT = 32
const BUCKET_MS = 5000
const PLAYHEAD_PX = 2
const FALLBACK_WIDTH = 600

const colors = () => {
  const style = getComputedStyle(document.documentElement)
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback
  return {
    typed: read('--c-cream', '#fae2c4'),
    deleted: read('--c-brick', '#a83e3a'),
    stamp: read('--c-dusk-bright', '#b49be0'),
    playhead: read('--c-lamp', '#ffd460'),
    bg: read('--c-surface', '#1b2040'),
  }
}

const drawBuckets = (
  ctx: CanvasRenderingContext2D,
  buckets: readonly StripBucket[],
  width: number,
  palette: ReturnType<typeof colors>,
): void => {
  const max = Math.max(1, ...buckets.map((b) => b.typedChars + b.stampChars + b.deletedChars))
  const colWidth = width / Math.max(1, buckets.length)
  buckets.forEach((bucket, i) => {
    const x = i * colWidth
    const w = Math.max(1, colWidth - 1)
    const typedH = (bucket.typedChars / max) * HEIGHT
    const stampH = (bucket.stampChars / max) * HEIGHT
    const deletedH = (bucket.deletedChars / max) * HEIGHT
    ctx.fillStyle = palette.typed
    ctx.fillRect(x, HEIGHT - typedH, w, typedH)
    ctx.fillStyle = palette.stamp
    ctx.fillRect(x, HEIGHT - typedH - stampH, w, stampH)
    ctx.fillStyle = palette.deleted
    ctx.fillRect(x, 0, w, deletedH)
  })
}

/** The bucket bars, painted once per size and reused under the moving playhead. */
const paintLayer = (
  buckets: readonly StripBucket[],
  width: number,
  dpr: number,
): HTMLCanvasElement | null => {
  const layer = document.createElement('canvas')
  layer.width = width * dpr
  layer.height = HEIGHT * dpr
  const ctx = layer.getContext('2d')
  if (!ctx) return null
  ctx.scale(dpr, dpr)
  const palette = colors()
  ctx.fillStyle = palette.bg
  ctx.fillRect(0, 0, width, HEIGHT)
  drawBuckets(ctx, buckets, width, palette)
  return layer
}

/** Chars typed per 5 s (from the bottom), deletions (from the top), stamps tinted. */
export const createTypingStrip = (log: KeystrokeLog): TypingStrip => {
  const buckets = typingStrip(log, BUCKET_MS)
  const realMs = Math.max(1, buckets.length * BUCKET_MS)
  const canvas = el('canvas', {
    class: 'typing-strip',
    role: 'img',
    'aria-label': 'typing speed and corrections over time',
  })
  canvas.style.height = `${HEIGHT}px`
  let playhead = 0
  let painted = -1
  let layer: HTMLCanvasElement | null = null
  let layerKey = ''

  const ensureLayer = (width: number, dpr: number): void => {
    const key = `${width}x${dpr}`
    if (key === layerKey && layer) return
    layerKey = key
    canvas.width = width * dpr
    canvas.height = HEIGHT * dpr
    layer = paintLayer(buckets, width, dpr)
    painted = -1
  }

  const paint = (): void => {
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = Math.max(1, window.devicePixelRatio || 1)
    const width = canvas.clientWidth || FALLBACK_WIDTH
    ensureLayer(width, dpr)
    if (!layer || painted === playhead) return
    painted = playhead
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.drawImage(layer, 0, 0)
    ctx.scale(dpr, dpr)
    ctx.fillStyle = colors().playhead
    const x = Math.min(width - PLAYHEAD_PX, (playhead / realMs) * width)
    ctx.fillRect(x, 0, PLAYHEAD_PX, HEIGHT)
  }

  requestAnimationFrame(paint)
  return {
    element: canvas,
    setPlayhead: (ms) => {
      playhead = ms
      paint()
    },
  }
}
